import { useState } from 'react'

const IconWrap = ({ children }) => (
  <div className="w-10 h-10 rounded-lg bg-white/5 shadow-[0_2px_8px_rgba(0,0,0,0.1)] flex items-center justify-center shrink-0">
    {children}
  </div>
)

const stroke = { stroke: '#ffffff', strokeWidth: 1.5, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }

const ArvIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </svg>
)

const FlipIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 8h12l-3-3" />
    <path d="M20 16H8l3 3" />
  </svg>
)

const ScoreIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <path d="M3 13a9 9 0 0118 0" />
    <path d="M12 13l4-4" />
    <circle cx="12" cy="13" r="1.2" fill="#ffffff" />
  </svg>
)

const MarketIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 20h16" />
    <rect x="6" y="11" width="3" height="7" />
    <rect x="11" y="7" width="3" height="11" />
    <rect x="16" y="4" width="3" height="14" />
  </svg>
)

const TemplatesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <rect x="6" y="4" width="12" height="14" rx="2" />
    <path d="M4 6v14a2 2 0 002 2h10" />
  </svg>
)

const DealTrackingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 6h16M4 12h16M4 18h10" />
    <circle cx="20" cy="18" r="2" fill="#ffffff" />
  </svg>
)

const NotesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <rect x="5" y="4" width="14" height="16" rx="2" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </svg>
)

const PropertyDashboardIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <rect x="3" y="4" width="18" height="11" rx="2" />
    <path d="M12 15v5M8 20h8" />
    <path d="M7 11l3-3 2 2 4-4" />
  </svg>
)

const CustomTagsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <path d="M3 12V5a2 2 0 012-2h7l9 9-9 9-9-9z" />
    <circle cx="8" cy="8" r="1.4" fill="#ffffff" />
  </svg>
)

const PropertyDataIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...stroke}>
    <ellipse cx="12" cy="6" rx="7" ry="2.5" />
    <path d="M5 6v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6" />
    <path d="M5 12v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-6" />
  </svg>
)

const ANALYSIS_TOOLS = [
  { name: 'ARV Analysis', Icon: ArvIcon },
  { name: 'Flip & BRRRR Projection', Icon: FlipIcon },
  { name: 'Deal Scoring', Icon: ScoreIcon },
  { name: 'Templates', Icon: TemplatesIcon },
]

const PIPELINE_TOOLS = [
  { name: 'Deal Tracking', Icon: DealTrackingIcon },
  { name: 'Notes', Icon: NotesIcon },
  { name: 'Property Dashboard', Icon: PropertyDashboardIcon },
  { name: 'Custom Tags', Icon: CustomTagsIcon },
  { name: 'Property Data', Icon: PropertyDataIcon },
]

const TOOLS_BY_TAB = {
  analysis: ANALYSIS_TOOLS,
  pipeline: PIPELINE_TOOLS,
}

const TAB_LABEL = {
  analysis: 'Deal Analysis',
  pipeline: 'Deal Pipeline',
}

const TABS = [
  { id: 'analysis', label: 'Deal Analysis' },
  { id: 'pipeline', label: 'Deal Pipeline' },
]

const ToolItem = ({ name, Icon }) => (
  <div className="flex items-center gap-4">
    <IconWrap>
      <Icon />
    </IconWrap>
    <span className="text-[20px] leading-[32px] font-bold text-white whitespace-nowrap">
      {name}
    </span>
  </div>
)

const Tools = () => {
  const [activeTab, setActiveTab] = useState('analysis')

  const tools = TOOLS_BY_TAB[activeTab]

  return (
    <section className="bg-brand-dark relative overflow-hidden">
      <div className="absolute -left-[40px] top-[206px] w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,_rgba(76,188,24,0.18),_transparent_70%)] pointer-events-none" />
      <div className="absolute right-[260px] top-[373px] w-[200px] h-[200px] rounded-full bg-[radial-gradient(circle,_rgba(76,188,24,0.12),_transparent_70%)] pointer-events-none" />

      <div className="relative z-10 max-w-canvas mx-auto pt-[98px] pb-[40px] flex flex-col items-center">
        <h2 className="text-[40px] leading-[48px] font-extrabold text-white text-center">
          All the Tools.{' '}
          <span className="text-brand-green">One Smarter Workflow.</span>
        </h2>

        <div className="mt-[37px] flex gap-2">
          {TABS.map(({ id, label }) => {
            const active = activeTab === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`px-6 py-3 rounded-full border text-[16px] font-semibold leading-6 text-brand-greenSec transition-colors ${
                  active
                    ? 'bg-[#213b13] border-[#2a7a05]'
                    : 'bg-brand-dark border-[#3b4238] hover:border-[#2a7a05]'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>

        <div className="mt-[18px] w-[960px] h-[330px] bg-white/5 border border-white/10 rounded-t-3xl shadow-[0_0_10px_rgba(0,0,0,0.04)] relative overflow-hidden">
          <p className="absolute left-1/2 -translate-x-1/2 top-[47px] text-[28px] leading-[40px] font-extrabold text-[#7d8c74] text-center w-[732px]">
            {TAB_LABEL[activeTab]}
          </p>

          <div className="absolute left-1/2 -translate-x-1/2 top-[110px] grid grid-cols-3 gap-x-[72px] gap-y-8 w-[760px]">
            {tools.map(({ name, Icon }) => (
              <ToolItem key={name} name={name} Icon={Icon} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Tools
